import { useCallback, useState } from 'react';
import { cn, Stack } from '@prenuvo/halo';
import { useShallow } from 'zustand/react/shallow';

import { usePriorStudies } from '@/shared/hook';
import {
  useComparisonKeyImageSelectionStore,
  useComparisonStore,
  usePreferencesModalStore,
} from '@/store';

import { ActivityLogsSidePanel } from './activity-logs-side-panel';
import { CarePlanServiceInfo } from './care-plan-service-info.component';
import { CopyPatientDetails } from './copy-patient-details.component';
import { HeaderLogo } from './header-logo.component';
import { HeaderMenu } from './header-menu';
import { PatientAlias } from './patient-alias.component';
import { PatientContact } from './patient-contact';
import { PatientDocsSidePanel } from './patient-docs-side-panel';
import { PatientGender } from './patient-gender.component';
import { PreferencesModal } from './preferences';
import { PrenuvoId } from './prenuvo-id.component';
import { PriorsButton } from './priors-button.component';
import { ResetReportButton } from './reset-report-button.component';
import { ResponsiveItems } from './responsive-items';
import { StaticItems } from './static-items.component';
import { StudyModeIndicator } from './study-mode-indicator.component';

interface HeaderProps {
  className?: string;
  showActions?: boolean;
}

export function Header({ className, showActions = true }: HeaderProps) {
  const [isActivityLogsSidePanelOpen, setIsActivityLogsSidePanelOpen] = useState(false);
  const [isPatientDocsOpen, setIsPatientDocsOpen] = useState(false);
  const {
    isOpen: isPreferencesModalOpen,
    open: openPreferencesModal,
    setOpen: setPreferencesModalOpen,
  } = usePreferencesModalStore(
    useShallow((state) => ({
      isOpen: state.isOpen,
      open: state.open,
      setOpen: state.setOpen,
    })),
  );
  const { hasPreviousStudyResults, priorStudyCount } = usePriorStudies();

  const { close, isOpen, open } = useComparisonStore(
    useShallow((state) => ({
      close: state.close,
      isOpen: state.isOpen,
      open: state.open,
    })),
  );
  const resetKeyImageSelection = useComparisonKeyImageSelectionStore((state) => state.reset);

  const isComparisonSplitOpen = isOpen && showActions;

  const closeComparison = useCallback(() => {
    resetKeyImageSelection();
    close();
  }, [close, resetKeyImageSelection]);

  const handlePatientDocsSidePanelOpenChange = useCallback(
    (value: boolean) => {
      if (isActivityLogsSidePanelOpen) {
        setIsActivityLogsSidePanelOpen(false);
      }

      if (value && isComparisonSplitOpen) {
        closeComparison();
      }

      setIsPatientDocsOpen(value);
    },
    [isActivityLogsSidePanelOpen, isComparisonSplitOpen, closeComparison],
  );

  const handleActivityLogsSidePanelOpenChange = useCallback(
    (value: boolean) => {
      if (isPatientDocsOpen) {
        setIsPatientDocsOpen(false);
      }

      if (value && isComparisonSplitOpen) {
        closeComparison();
      }

      setIsActivityLogsSidePanelOpen(value);
    },
    [isPatientDocsOpen, isComparisonSplitOpen, closeComparison],
  );

  const handleComparisonOpenChange = useCallback(() => {
    if (!showActions) {
      return;
    }

    if (isOpen) {
      closeComparison();

      return;
    }

    if (isPatientDocsOpen) {
      setIsPatientDocsOpen(false);
    }

    if (isActivityLogsSidePanelOpen) {
      setIsActivityLogsSidePanelOpen(false);
    }

    open();
  }, [showActions, isOpen, closeComparison, isPatientDocsOpen, isActivityLogsSidePanelOpen, open]);

  return (
    <>
      <Stack
        alignment="center"
        className={cn(
          'bg-fct-background dark:bg-fct-background relative z-50 h-16 rounded-t-2xl px-4 text-neutral-400 dark:text-neutral-400',
          className,
        )}
        data-testid="header-container"
        role="banner"
      >
        <HeaderLogo />

        <div className="flex items-center">
          <CopyPatientDetails />
        </div>
        <div className={cn('flex flex-1 flex-col')}>
          <div className="items-center">
            <span className={cn('flex items-center pt-0.5 pl-1 text-sm leading-none')}>
              <PatientAlias />

              <PatientContact />

              <PatientGender />

              <PrenuvoId />

              <StaticItems />

              <ResponsiveItems />
            </span>
          </div>

          <CarePlanServiceInfo />
        </div>

        <div className="flex items-center gap-2">
          <StudyModeIndicator />

          {showActions ? (
            <>
              <ResetReportButton />

              <PatientDocsSidePanel
                isOpen={isPatientDocsOpen}
                onOpenChange={handlePatientDocsSidePanelOpenChange}
              />

              {hasPreviousStudyResults ? (
                <PriorsButton
                  isActive={isComparisonSplitOpen}
                  onClick={handleComparisonOpenChange}
                  priorStudyCount={priorStudyCount}
                />
              ) : null}

              <HeaderMenu
                onOpenActivityLogs={() =>
                  handleActivityLogsSidePanelOpenChange(!isActivityLogsSidePanelOpen)
                }
                onOpenPreferences={openPreferencesModal}
              />
            </>
          ) : null}
        </div>
      </Stack>

      {showActions ? (
        <>
          <ActivityLogsSidePanel
            isOpen={isActivityLogsSidePanelOpen}
            onClose={() => handleActivityLogsSidePanelOpenChange(false)}
          />

          <PreferencesModal onOpenChange={setPreferencesModalOpen} open={isPreferencesModalOpen} />
        </>
      ) : null}
    </>
  );
}
